/**
 * GlobalMismatchChecker
 *
 * Runs on ALL pages to detect when user is at a different job than clocked into.
 * Listens for app resume and checks location mismatch after 5+ minutes.
 * Shows modal to switch jobs regardless of which page user is on.
 *
 * Also listens for background job switches (from background-geolocation.service.ts)
 * and provides a PWA fallback with periodic location checking when clocked in.
 */

import React, { useState, useEffect, useContext, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { AuthContext } from '../contexts/AuthContext';
import { useLocation } from '../contexts/LocationContext';
import { useLocationMismatch } from '../hooks/useLocationMismatch';
import { JobMismatchModal } from './common/JobMismatchModal';
import { backgroundGeolocationService } from '../services/background-geolocation.service';
import { Job } from '../types/backend.types';
import api from '../api/auth.api';
import { API_CONFIG } from '../config/api.config';

// PWA fallback: check location every 5 minutes when clocked in
const PWA_CHECK_INTERVAL_MS = 5 * 60 * 1000;

// AUTO-SWITCH POPUP RETIRED (2026-06-18): the location-mismatch modal that
// auto-prompted workers to switch jobs is disabled. This kills the only
// remaining *automatic* job switch in the PWA (the background DWELL switch was
// already retired in background-geolocation.service.ts). What still works:
//   - Manual switching from the Time Clock (switch_source:'manual') — untouched
//   - ENTER/EXIT geofence timeline logging + tracking restart on resume
// When false: never detect a mismatch, never show the modal, and skip the
// 5-min PWA location poll (its only purpose was feeding mismatch detection).
// (To revive auto-prompting: flip this back to true.)
const AUTO_SWITCH_PROMPT_ENABLED = false;

export const GlobalMismatchChecker: React.FC = () => {
  const auth = useContext(AuthContext);
  const { location, ensureFreshLocation } = useLocation();

  const [jobs, setJobs] = useState<Job[]>([]);
  const [activeTimeEntry, setActiveTimeEntry] = useState<any | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [detectedJob, setDetectedJob] = useState<Job | null>(null);
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const [ignoredJobId, setIgnoredJobId] = useState<number | null>(null);
  const [language, setLanguage] = useState<'en' | 'es'>(() => {
    return (localStorage.getItem('language') as 'en' | 'es') || 'en';
  });

  // Ref for PWA fallback interval
  const pwaCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Ref to track when modal was recently closed (prevents immediate re-show)
  const modalClosedAtRef = useRef<number>(0);

  // Use the location mismatch hook
  const { hasMismatch, detectedJob: mismatchDetectedJob, isLocationStale } = useLocationMismatch(
    activeTimeEntry?.job_id || null,
    jobs
  );

  // Load jobs and clock status on mount, also ensure fresh location
  useEffect(() => {
    if (!auth?.user?.employee_id) return;

    const loadData = async () => {
      try {
        // Start location refresh early (don't await - let it run in parallel)
        // This speeds up mismatch detection when app opens with stale location
        const locationPromise = ensureFreshLocation().catch(err => {
          console.error('[GlobalMismatchChecker] Error refreshing location:', err);
        });

        // Load jobs and clock status
        const [jobsResponse, clockResponse] = await Promise.all([
          api.get(API_CONFIG.ENDPOINTS.JOBS),
          api.get(`${API_CONFIG.ENDPOINTS.TIME}/current`)
        ]);

        if (jobsResponse.data.success) {
          setJobs(jobsResponse.data.data);
        }

        if (clockResponse.data.success && clockResponse.data.data?.job_id) {
          const clockData = clockResponse.data.data;
          setActiveTimeEntry(clockData);

          // Ensure TransistorSoft is running if user is clocked in (on app startup)
          if (Capacitor.isNativePlatform()) {
            backgroundGeolocationService.ensureTrackingActive(
              {
                job_id: clockData.job_id,
                entry_id: clockData.entry_id,
                employee_id: auth?.user?.employee_id
              },
              auth?.user?.role,
              true,
              true
            ).catch(err => {
              console.error('[GlobalMismatchChecker] Error ensuring tracking active:', err);
            });
          }
        } else {
          setActiveTimeEntry(null);
        }

        // Wait for location to finish if not already done
        await locationPromise;
      } catch (error) {
        console.error('[GlobalMismatchChecker] Error loading data:', error);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.user?.employee_id]);

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = (event: Event) => {
      const newLang = (event as CustomEvent).detail;
      if (newLang === 'en' || newLang === 'es') {
        setLanguage(newLang);
      }
    };

    window.addEventListener('languageChange', handleLanguageChange);
    return () => window.removeEventListener('languageChange', handleLanguageChange);
  }, []);

  // Listen for background job switch events (from background-geolocation.service.ts)
  useEffect(() => {
    const handleBackgroundJobSwitch = async (event: Event) => {
      const detail = (event as CustomEvent).detail;
      console.log('[GlobalMismatchChecker] Background job switch event:', detail);

      // Update active time entry to reflect the new job
      if (detail?.newJobId) {
        setActiveTimeEntry((prev: any) => prev ? { ...prev, job_id: detail.newJobId } : null);

        // Refresh full clock status to get complete data
        try {
          const clockResponse = await api.get(`${API_CONFIG.ENDPOINTS.TIME}/current`);
          if (clockResponse.data.success && clockResponse.data.data?.job_id) {
            setActiveTimeEntry(clockResponse.data.data);
          }
        } catch (error) {
          console.error('[GlobalMismatchChecker] Error refreshing clock status after background switch:', error);
        }
      }
    };

    window.addEventListener('backgroundJobSwitch', handleBackgroundJobSwitch);
    return () => window.removeEventListener('backgroundJobSwitch', handleBackgroundJobSwitch);
  }, []);

  // PWA fallback: periodic location check when clocked in (for web platform only)
  useEffect(() => {
    // Auto-switch popup is retired — this poll only fed mismatch detection, so skip it.
    // Only run for web platform (PWA) when user is clocked in
    if (!AUTO_SWITCH_PROMPT_ENABLED || Capacitor.isNativePlatform() || !auth?.user?.employee_id || !activeTimeEntry) {
      // Clear any existing interval
      if (pwaCheckIntervalRef.current) {
        clearInterval(pwaCheckIntervalRef.current);
        pwaCheckIntervalRef.current = null;
      }
      return;
    }

    console.log('[GlobalMismatchChecker] Starting PWA fallback location check interval');

    // Set up periodic location check
    pwaCheckIntervalRef.current = setInterval(async () => {
      console.log('[GlobalMismatchChecker] PWA fallback: checking location...');
      await ensureFreshLocation();
    }, PWA_CHECK_INTERVAL_MS);

    return () => {
      if (pwaCheckIntervalRef.current) {
        clearInterval(pwaCheckIntervalRef.current);
        pwaCheckIntervalRef.current = null;
      }
    };
  }, [auth?.user?.employee_id, activeTimeEntry, ensureFreshLocation]);

  // Show mismatch modal when detected
  useEffect(() => {
    // Auto-switch popup is retired — never surface the mismatch modal.
    if (!AUTO_SWITCH_PROMPT_ENABLED) return;

    // Don't show modal if it was recently closed (prevents race condition re-show)
    const timeSinceClosed = Date.now() - modalClosedAtRef.current;
    if (timeSinceClosed < 2000) {
      console.log('[GlobalMismatchChecker] Modal recently closed, skipping re-show');
      return;
    }

    // Don't show modal if:
    // - No mismatch or no detected job
    // - Modal already showing
    // - Location is stale
    // - User ignored this job
    // - Detected job is same as clocked-in job (race condition after switch)
    if (
      hasMismatch &&
      mismatchDetectedJob &&
      !showModal &&
      !isLocationStale &&
      mismatchDetectedJob.job_id !== ignoredJobId &&
      mismatchDetectedJob.job_id !== activeTimeEntry?.job_id // Prevent re-show after switch
    ) {
      console.log('[GlobalMismatchChecker] Mismatch detected, showing modal');
      setDetectedJob(mismatchDetectedJob);
      setShowModal(true);
    }
  }, [hasMismatch, mismatchDetectedJob, showModal, isLocationStale, ignoredJobId, activeTimeEntry?.job_id]);

  // Reset ignored job when user moves to a different location
  useEffect(() => {
    if (!hasMismatch && ignoredJobId !== null) {
      console.log('[GlobalMismatchChecker] No longer at ignored job, resetting');
      setIgnoredJobId(null);
    }
  }, [hasMismatch, ignoredJobId]);

  // Listen for app resume to refresh location and re-check
  useEffect(() => {
    // Only run for native apps AND when user is logged in
    if (!Capacitor.isNativePlatform() || !auth?.user?.employee_id) return;

    console.log('[GlobalMismatchChecker] Setting up app resume listener');

    const setupListeners = async () => {
      const resumeListener = await App.addListener('resume', async () => {
        // Double-check user is still logged in
        if (!auth?.user?.employee_id) {
          console.log('[GlobalMismatchChecker] App resumed but user not logged in, skipping');
          return;
        }

        console.log('[GlobalMismatchChecker] App resumed, checking location...');
        await ensureFreshLocation(); // Only refreshes if > 5 min old

        // Refresh clock status in case it changed
        try {
          const clockResponse = await api.get(`${API_CONFIG.ENDPOINTS.TIME}/current`);
          if (clockResponse.data.success && clockResponse.data.data?.job_id) {
            const clockData = clockResponse.data.data;
            setActiveTimeEntry(clockData);

            // Ensure TransistorSoft is running if user is clocked in
            await backgroundGeolocationService.ensureTrackingActive(
              {
                job_id: clockData.job_id,
                entry_id: clockData.entry_id,
                employee_id: auth?.user?.employee_id
              },
              auth?.user?.role,
              true, // trackingEnabled - could get from user settings if needed
              true  // autoSwitchEnabled - could get from user settings if needed
            );
          } else {
            setActiveTimeEntry(null);
          }
        } catch (error) {
          console.error('[GlobalMismatchChecker] Error refreshing clock status:', error);
        }
      });

      const stateListener = await App.addListener('appStateChange', async ({ isActive }) => {
        // Double-check user is still logged in
        if (!isActive || !auth?.user?.employee_id) {
          return;
        }

        console.log('[GlobalMismatchChecker] App became active, checking location...');
        await ensureFreshLocation();

        // Refresh clock status
        try {
          const clockResponse = await api.get(`${API_CONFIG.ENDPOINTS.TIME}/current`);
          if (clockResponse.data.success && clockResponse.data.data?.job_id) {
            const clockData = clockResponse.data.data;
            setActiveTimeEntry(clockData);

            // Ensure TransistorSoft is running if user is clocked in
            await backgroundGeolocationService.ensureTrackingActive(
              {
                job_id: clockData.job_id,
                entry_id: clockData.entry_id,
                employee_id: auth?.user?.employee_id
              },
              auth?.user?.role,
              true,
              true
            );
          } else {
            setActiveTimeEntry(null);
          }
        } catch (error) {
          console.error('[GlobalMismatchChecker] Error refreshing clock status:', error);
        }
      });

      return () => {
        resumeListener.remove();
        stateListener.remove();
      };
    };

    const cleanupPromise = setupListeners();
    return () => {
      cleanupPromise.then(cleanup => cleanup?.());
    };
  }, [ensureFreshLocation, auth?.user?.employee_id]);

  // Handle job switch
  const handleSwitch = async () => {
    if (!detectedJob || !activeTimeEntry) return;

    setSwitching(true);
    setSwitchError(null);
    try {
      // Get fresh location before switching to ensure accurate coordinates
      const freshLocation = await ensureFreshLocation();

      const timezoneOffsetMinutes = new Date().getTimezoneOffset();
      const SHOP_JOB_IDS = [1, 2];
      const clockedIntoShop = SHOP_JOB_IDS.includes(activeTimeEntry.job_id);
      const isTemporaryShopClock = activeTimeEntry.clocked_at_shop && !activeTimeEntry.shop_work_all_day;
      const conversionReason = clockedIntoShop ? 'shop_to_job' : 'job_to_job';
      const billingMethod = isTemporaryShopClock ? 'bill_all' : 'split';

      const response = await api.post(API_CONFIG.ENDPOINTS.TIME_ENTRIES + '/switch-job', {
        job_id: detectedJob.job_id,
        lat: freshLocation?.latitude || location?.latitude || null,
        lng: freshLocation?.longitude || location?.longitude || null,
        conversion_reason: conversionReason,
        timezone_offset_minutes: timezoneOffsetMinutes,
        billing_method: billingMethod,
        switch_source: 'location_mismatch'
      });

      if (response.data.success) {
        console.log('[GlobalMismatchChecker] Successfully switched to job:', detectedJob.job_name);

        // IMPORTANT: Update activeTimeEntry FIRST to prevent mismatch effect from re-triggering
        // Set to the new job immediately so hasMismatch becomes false
        setActiveTimeEntry({ ...activeTimeEntry, job_id: detectedJob.job_id });

        // Update background geolocation service with new job, entry ID, and mark as handled
        backgroundGeolocationService.updateCurrentJob(detectedJob.job_id);
        if (response.data.data?.new_entry_id) {
          backgroundGeolocationService.updateCurrentEntryId(response.data.data.new_entry_id);
        }
        backgroundGeolocationService.markJobAsHandled(detectedJob.job_id);

        // Now close modal - mismatch effect won't re-show it because job_id matches
        modalClosedAtRef.current = Date.now();
        setShowModal(false);
        setDetectedJob(null);

        // Dispatch event so dashboards can update
        window.dispatchEvent(new CustomEvent('jobSwitched', { detail: detectedJob }));

        // Refresh full clock status in background
        try {
          const clockResponse = await api.get(`${API_CONFIG.ENDPOINTS.TIME}/current`);
          if (clockResponse.data.success) {
            setActiveTimeEntry(clockResponse.data.data);
          }
        } catch (err) {
          console.error('[GlobalMismatchChecker] Error refreshing clock status:', err);
        }
      } else if (response.data.error === 'Already clocked into this job') {
        // Background service already performed this switch — just close the modal
        console.log('[GlobalMismatchChecker] Job already switched by background service, closing modal');
        setActiveTimeEntry({ ...activeTimeEntry, job_id: detectedJob.job_id });
        backgroundGeolocationService.markJobAsHandled(detectedJob.job_id);
        modalClosedAtRef.current = Date.now();
        setShowModal(false);
        setDetectedJob(null);
      } else {
        console.error('[GlobalMismatchChecker] Failed to switch job:', response.data);
        setSwitchError('Failed to switch job. Please try again or use the Time Clock.');
      }
    } catch (error: any) {
      // Check if the error is "Already clocked into this job" from a 400 response
      const errorMsg = error?.response?.data?.error || error?.message || '';
      if (errorMsg.includes('Already clocked into this job')) {
        console.log('[GlobalMismatchChecker] Job already switched by background service, closing modal');
        setActiveTimeEntry({ ...activeTimeEntry, job_id: detectedJob.job_id });
        backgroundGeolocationService.markJobAsHandled(detectedJob.job_id);
        modalClosedAtRef.current = Date.now();
        setShowModal(false);
        setDetectedJob(null);
      } else {
        console.error('[GlobalMismatchChecker] Error switching job:', error);
        setSwitchError('Error switching job. Please try again or use the Time Clock.');
      }
    } finally {
      setSwitching(false);
    }
  };

  // Handle ignore - remember this job so we don't pester again
  const handleIgnore = () => {
    if (detectedJob) {
      console.log('[GlobalMismatchChecker] User ignored mismatch for job:', detectedJob.job_name);
      setIgnoredJobId(detectedJob.job_id);

      // Also notify background geolocation service so it doesn't auto-switch
      backgroundGeolocationService.markJobAsHandled(detectedJob.job_id);
    }
    modalClosedAtRef.current = Date.now();
    setShowModal(false);
    setDetectedJob(null);
    setSwitchError(null);
  };

  // Don't render anything if not logged in or no active time entry
  // Truck drivers handle job switches automatically via drops - no popup needed
  if (!auth?.user || !activeTimeEntry || auth?.user?.role === 'cdl_driver' || auth?.user?.role === 'truck_driver') {
    return null;
  }

  // Render modal if showing
  if (!showModal || !detectedJob) {
    return null;
  }

  const clockedInJob = jobs.find(j => j.job_id === activeTimeEntry.job_id);

  return (
    <JobMismatchModal
      detectedJob={detectedJob}
      clockedInJob={{
        job_id: activeTimeEntry.job_id,
        job_name: clockedInJob?.job_name || `Job #${activeTimeEntry.job_id}`
      }}
      onSwitch={handleSwitch}
      onIgnore={handleIgnore}
      language={language}
      switching={switching}
      error={switchError}
    />
  );
};
